import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Crown, Zap } from "lucide-react";

/**
 * Founder Seller 14-day 0%-fee countdown banner.
 *
 * Renders ONLY when the current user is an approved Founder Seller AND
 * their `founder_since` date is within the 14-day fee-free window.
 * Quietly returns null in every other case (non-founder, buyer, expired
 * window) so the surface stays clean.
 *
 * Polls `/api/me/founder-status` once on mount. The day counter recomputes
 * locally every minute so the badge feels alive without hammering the API.
 */
export default function FounderCountdownBanner() {
  const [status, setStatus] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/me/founder-status");
        if (!cancelled) setStatus(data);
      } catch (_e) {
        /* unauth or not a founder — banner stays hidden */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Re-render every 60s so the day/hour counter updates locally.
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  const live = useMemo(() => {
    if (!status?.founder_active || !status?.founder_expires_at) return null;
    const ends = new Date(status.founder_expires_at).getTime();
    const now = Date.now();
    const ms = ends - now;
    if (ms <= 0) return null;
    const totalHours = Math.floor(ms / 3_600_000);
    const days = Math.floor(totalHours / 24);
    const hours = totalHours - days * 24;
    const totalDays = status.fee_free_total_days || 14;
    const elapsedDays = Math.max(0, totalDays - days - (hours > 0 ? 1 : 0));
    const pct = Math.min(100, Math.max(6, (elapsedDays / totalDays) * 100));
    return { days, hours, pct, totalDays, ends };
    // tick is a dependency so the memo recomputes each minute
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, tick]);

  if (!live) return null;

  return (
    <div
      data-testid="founder-countdown-banner"
      className="relative overflow-hidden rounded-2xl border border-amber-300/60 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 px-5 py-4 mb-6 shadow-sm"
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 grid place-items-center text-white shadow-md flex-shrink-0">
          <Crown size={22} strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-black tracking-[0.18em] uppercase text-amber-700">
              Founder Seller benefit
            </span>
            <span
              className="inline-flex items-center gap-1 rounded-full bg-zinc-900 text-amber-300 px-2 py-0.5 text-[10px] font-bold"
              data-testid="founder-countdown-pill"
            >
              <Zap size={10} fill="currentColor" strokeWidth={0} /> 0% fees active
            </span>
          </div>
          <div className="font-display font-black text-xl text-zinc-900 mt-0.5 tracking-tight">
            <span data-testid="founder-countdown-days">{live.days}</span>{" "}
            <span className="text-zinc-500 font-extrabold">days</span>
            {live.days < 3 && (
              <>
                {" "}
                <span data-testid="founder-countdown-hours">{live.hours}</span>{" "}
                <span className="text-zinc-500 font-extrabold">hrs</span>
              </>
            )}{" "}
            <span className="text-zinc-500 font-normal text-base">of 0% commission left</span>
          </div>
          <div className="text-xs text-zinc-600 mt-1">
            Every dollar your buyers pay lands in your payout until{" "}
            <b className="text-zinc-900">
              {new Date(live.ends).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </b>
            . Make these {live.totalDays} days count.
          </div>
          {/* Progress bar — left-aligned, fills as the window elapses */}
          <div className="mt-3 h-1.5 w-full rounded-full bg-amber-200/70 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all"
              style={{ width: `${live.pct}%` }}
              data-testid="founder-countdown-progress"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
